import React, { useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import { useSiteTextContent } from "@Front/Stores/SiteTextsContext";
import classes from "./classes.module.scss";
import type { IOption } from "./DropdownOption";
import { DropdownMenuListbox } from "./DropdownMenuListbox";
import { useDropdownMenuHandlers } from "./hooks/useDropdownMenuHandlers";

type IProps = {
	options: IOption[];
	selectedOptions: IOption[];
	children: React.ReactNode;
	openable: {
		isOpen: boolean;
		open: () => void;
		close: () => void;
		toggle: () => void;
	};
	onSelect?: (newOption: IOption, options: IOption[]) => void;
	/** When false, selection keeps the menu open (multi-select). Default true. */
	closeOnSelect?: boolean;
	menuId?: string;
	listboxAccessibleName?: string;
};

export default function DropdownMenu(props: IProps): React.ReactElement {
	const { children, options, onSelect, openable, selectedOptions, closeOnSelect = true, menuId, listboxAccessibleName } = props;
	const ref = useRef<HTMLDivElement>(null);
	const defaultListboxName = useSiteTextContent("components.dropdown.defaultAriaLabel");
	const listboxName = listboxAccessibleName ?? defaultListboxName;
	const { handleSelect, handleClickOutside } = useDropdownMenuHandlers({
		ref,
		openable,
		selectedOptions,
		onSelect,
		closeOnSelect,
	});
	const isActive = useCallback((option: IOption): boolean => selectedOptions.some((s) => s.id === option.id), [selectedOptions]);
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return (): void => document.removeEventListener("mousedown", handleClickOutside);
	}, [handleClickOutside]);
	const rootClass = classNames([classes["root"], openable.isOpen && classes["open"]]);
	if (!openable.isOpen) {
		return (
			<div className={rootClass} ref={ref}>
				{children}
				<div className={classes["content"]} id={menuId} aria-label={listboxName} hidden aria-hidden="true" />
			</div>
		);
	}
	return (
		<div className={rootClass} ref={ref}>
			{children}
			<DropdownMenuListbox options={options} isActive={isActive} handleSelect={handleSelect} menuId={menuId} listboxName={listboxName} />
		</div>
	);
}
